"use client";

import { useState, useEffect, useRef } from "react";
import dynamic from "next/dynamic";
import Link from "next/link";
import { useAuth } from "@/hooks/useAuth";
import { useSidebar } from "@/contexts/SidebarContext";
import { motion } from "framer-motion";
import {
  FiCopy,
  FiCheck,
  FiTrendingUp,
  FiExternalLink,
  FiStar,
  FiAlertCircle,
  FiGlobe,
  FiShare2,
  FiPackage,
} from "react-icons/fi";
import { FaXTwitter, FaTelegram } from "react-icons/fa6";
import { UsersIcon } from "@/app/components/icons/UsersIcon";
import { DollarSignIcon } from "@/app/components/icons/DollarSignIcon";
import { TicketIcon } from "@/app/components/icons/TicketIcon";
import { CopyIcon } from "@/app/components/icons/CopyIcon";
import { authFetch } from "@/lib/authFetch";
import RequestPayoutModal from "./components/RequestPayoutModal";
import CdnImage from "@/components/CdnImage";

import AnimatedNumber from "@/app/components/AnimatedNumber";
import { useT } from "@/hooks/useT";
import { createLogger } from "@/lib/logger";

const logger = createLogger("AffiliateDashboard");

const AffiliateToolkit = dynamic(() => import("./components/AffiliateToolkit"), {
  ssr: false,
  loading: () => (
    <div className="flex items-center justify-center py-20">
      <div className="w-10 h-10 border-4 border-gray-300 dark:border-white/30 border-t-transparent rounded-full animate-spin" />
    </div>
  ),
});

interface Referral {
  id: string;
  wallet: string;
  signedUpAt: string;
  isActivated: boolean;
  totalVolume: number;
  totalCommission: number;
  source: 'web' | 'telegram' | 'both';
  level: number;
  parentWallet?: string | null;
}

interface Commission {
  id: string;
  amount: number;
  tradeVolume: number;
  commissionRate: number;
  level: number;
  status: string;
  earnedAt: string;
  referredUser: string | null;
  source?: 'polymarket' | 'kalshi' | 'predict' | 'copytrading' | 'manual' | string;
  commissionType?: string;
  sourcePostId?: string | null;
}

const SOURCE_META: Record<string, { label: string; icon: string }> = {
  polymarket: { label: 'Polymarket', icon: '/polymarket-logo-blue.png' },
  kalshi: { label: 'Kalshi', icon: '/kalshi-icon.png' },
  predict: { label: 'Predict.fun', icon: '/logos/predictfun/Predict-Icon-BP.png' },
};

function SourceBadge({ source }: { source?: string }) {
  const meta = SOURCE_META[source || ''] || SOURCE_META.polymarket;
  return (
    <span className="inline-flex items-center gap-1.5 text-xs font-inter-tight font-semibold text-gray-700 dark:text-gray-300">
      <CdnImage src={meta.icon} alt={meta.label} width={14} height={14} className="w-3.5 h-3.5 object-contain" />
      {meta.label}
    </span>
  );
}

interface ReferralTier {
  name: string;
  displayName: string;
  level1Percent: number;
  level2Percent: number;
  level3Percent: number;
  expiresAt: string | null;
}

interface ReferralStats {
  referralCode: string;
  totalReferrals: number;
  activeReferrals: number;
  totalClicks: number;
  clicksLast30Days: number;
  signupsLast30Days: number;
  earningsLast30Days: number;
  activatedSignups: number;
  totalEarnings: number;
  pendingPayout: number;
  totalPaidOut: number;
  availableBalance: number;
  conversionRate: number;
  commissionRate: number;
  totalFPEarned: number;
  fpCommissionRate: number;
  tier: ReferralTier;
}

interface AffiliateDashboardProps {
  currentPage?: 'dashboard' | 'toolkit';
}

function PayoutStatusBadge({ status }: { status: string }) {
  const t = useT();
  const styles: Record<string, string> = {
    requested: 'bg-yellow-500/10 text-yellow-600 dark:text-yellow-400',
    approved: 'bg-blue-500/10 text-blue-600 dark:text-blue-400',
    processing: 'bg-blue-500/10 text-blue-600 dark:text-blue-400',
    completed: 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400',
    rejected: 'bg-red-500/10 text-red-600 dark:text-red-400',
    failed: 'bg-red-500/10 text-red-600 dark:text-red-400',
  };
  const className = styles[status] || 'bg-gray-500/10 text-gray-500';
  const label = styles[status] ? t(`aff.dash.payoutStatus.${status}`) : status;
  return (
    <span className={`text-xs font-inter-tight font-semibold px-2 py-0.5 rounded-[5px] ${className}`}>
      {label}
    </span>
  );
}

export default function AffiliateDashboard({ currentPage = 'dashboard' }: AffiliateDashboardProps) {
  const t = useT();
  const { isLoading, isReady, isLoggedIn, walletAddress } = useAuth();
  const { isCollapsed } = useSidebar();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState<ReferralStats | null>(null);
  const [referrals, setReferrals] = useState<Referral[]>([]);
  const [commissions, setCommissions] = useState<Commission[]>([]);
  const [copied, setCopied] = useState(false);
  const [copiedTg, setCopiedTg] = useState(false);
  const [activeTab, setActiveTab] = useState<'referrals' | 'commissions' | 'payouts'>('referrals');
  const [error, setError] = useState<string | null>(null);
  const [hasMultiTier, setHasMultiTier] = useState(false);
  const hasTriedAutoCreate = useRef(false);

  // Payout state
  const [showPayoutModal, setShowPayoutModal] = useState(false);
  const [payoutHistory, setPayoutHistory] = useState<any[]>([]);


  useEffect(() => {
    if (!isReady) return;

    if (isLoggedIn) {
      fetchStats();
      fetchReferrals();
      fetchCommissions();
      fetchPayoutHistory();
    }
  }, [isReady, isLoggedIn]);

  // Default empty stats for when API fails (allows UI to render)
  const emptyStats: ReferralStats = {
    referralCode: '------',
    totalReferrals: 0,
    activeReferrals: 0,
    totalClicks: 0,
    clicksLast30Days: 0,
    signupsLast30Days: 0,
    earningsLast30Days: 0,
    activatedSignups: 0,
    totalEarnings: 0,
    pendingPayout: 0,
    totalPaidOut: 0,
    availableBalance: 0,
    conversionRate: 0,
    commissionRate: 10,
    totalFPEarned: 0,
    fpCommissionRate: 10,
    tier: {
      name: 'standard',
      displayName: 'Standard',
      level1Percent: 10,
      level2Percent: 0,
      level3Percent: 0,
      expiresAt: null,
    },
  };

  const fetchStats = async () => {
    try {
      setError(null);
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 10000);
      const response = await authFetch("/api/referral/stats", { signal: controller.signal });
      clearTimeout(timeoutId);

      if (!response.ok) {
        // On auth failure, use empty stats so UI still renders
        if (response.status === 401) {
          setStats(emptyStats);
          return;
        }
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `Stats request failed: ${response.status}`);
      }

      const data = await response.json();

      if (data.hasReferralCode) {
        setStats(data.stats);
      } else if (!hasTriedAutoCreate.current) {
        hasTriedAutoCreate.current = true;
        await createReferralCode();
      }
    } catch (err) {
      // On error, still show UI with empty stats
      setStats(emptyStats);
      const message = err instanceof Error
        ? (err.name === 'AbortError' ? t('aff.dash.error.timeout') : err.message)
        : t('aff.dash.error.loading');
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  const createReferralCode = async () => {
    try {
      const response = await authFetch("/api/referral/code");

      if (!response.ok) {
        // On auth failure, use empty stats so UI still renders
        if (response.status === 401) {
          setStats(emptyStats);
          return;
        }
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `Code creation failed: ${response.status}`);
      }

      // Code created, now fetch stats again
      const statsResponse = await authFetch("/api/referral/stats");
      if (statsResponse.ok) {
        const data = await statsResponse.json();
        if (data.hasReferralCode) {
          setStats(data.stats);
        }
      } else {
        // Failed to get stats after creating code, show empty
        setStats(emptyStats);
      }
    } catch (err) {
      // On error, still show UI with empty stats
      setStats(emptyStats);
      const message = err instanceof Error ? err.message : t('aff.dash.error.creatingCode');
      setError(message);
    }
  };

  const fetchReferrals = async () => {
    try {
      const response = await authFetch("/api/referral/referrals");
      if (response.ok) {
        const data = await response.json();
        setReferrals(data.referrals || []);
        setHasMultiTier(data.hasMultiTier || false);
      }
    } catch (err) {
      logger.error("Failed to fetch referrals", { error: err instanceof Error ? err.message : String(err) });
    }
  };

  const fetchCommissions = async () => {
    try {
      const response = await authFetch("/api/referral/commissions");
      if (response.ok) {
        const data = await response.json();
        setCommissions(data.commissions || []);
      }
    } catch (err) {
      logger.error("Failed to fetch commissions", { error: err instanceof Error ? err.message : String(err) });
    }
  };

  const fetchPayoutHistory = async () => {
    try {
      const response = await authFetch("/api/referral/payout/history");
      if (response.ok) {
        const data = await response.json();
        setPayoutHistory(data.payouts || []);
      }
    } catch (err) {
      logger.error("Failed to fetch payout history", { error: err instanceof Error ? err.message : String(err) });
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const getReferralLink = () => {
    if (!stats) return '';
    return `${typeof window !== 'undefined' ? window.location.origin : 'https://frenflow.com'}?fren=${stats.referralCode}`;
  };

  // Telegram deep link → opens the FrenFlow login bot directly with the
  // referral code attached, so invitees stay inside Telegram instead of
  // bouncing to the web. The "ref_" prefix is mandatory: it's how Telegram
  // passes the start parameter to the bot, and how the bot detects a
  // referral (telegram-login-ui/src/bot/index.ts).
  const getTelegramBotLink = () => {
    if (!stats) return '';
    return `https://t.me/FrenFlowLoginBot?start=ref_${stats.referralCode}`;
  };

  const copyReferralLink = () => {
    if (!stats) return;
    navigator.clipboard.writeText(getReferralLink());
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const copyTelegramBotLink = () => {
    if (!stats) return;
    navigator.clipboard.writeText(getTelegramBotLink());
    setCopiedTg(true);
    setTimeout(() => setCopiedTg(false), 2000);
  };

  const shareToTwitter = () => {
    const text = t('aff.dash.tweetCopy');
    const url = getReferralLink();
    window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`, '_blank');
  };

  const shareToTelegram = () => {
    // Share the bot deep link, not the web URL. If the receiver is
    // already on Telegram, the best UX is keeping them in Telegram —
    // tapping the link opens @FrenFlowLoginBot directly and applies the
    // referral via the bot's /start handler.
    const text = t('aff.dash.telegramCopy');
    const url = getTelegramBotLink();
    window.open(`https://t.me/share/url?url=${encodeURIComponent(url)}&text=${encodeURIComponent(text)}`, '_blank');
  };

  if (loading || isLoading) {
    return (
      <div className="fixed inset-0 z-40 bg-gray-50 dark:bg-[#0f0f12]">
        <div className={`absolute inset-0 transition-[left] duration-300 ${isCollapsed ? 'lg:left-20' : 'lg:left-64'}`}>
          <div className="h-14 lg:h-16" />
          <div className="flex items-center justify-center h-[calc(100dvh-4rem)]">
            <div className="w-12 h-12 border-4 border-gray-300 dark:border-white/30 border-t-transparent rounded-full animate-spin" />
          </div>
        </div>
      </div>
    );
  }

  if (!stats) {
    return (
      <div className="fixed inset-0 z-40 bg-gray-50 dark:bg-[#0f0f12]">
        <div className={`absolute inset-0 transition-[left] duration-300 ${isCollapsed ? 'lg:left-20' : 'lg:left-64'}`}>
          <div className="h-14 lg:h-16" />
          <div className="flex items-center justify-center h-[calc(100dvh-4rem)]">
            <div className="max-w-md mx-auto px-4">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-white dark:bg-[#18181D] rounded-[5px] border border-gray-200 dark:border-[#1A1A1A] p-12 text-center"
              >
                {error ? (
                  <>
                    <div className="w-12 h-12 mx-auto mb-4 rounded-[5px] bg-red-500/10 flex items-center justify-center">
                      <FiAlertCircle className="w-6 h-6 text-red-500" />
                    </div>
                    <p className="text-red-500 font-satoshi mb-4">{error}</p>
                    <button
                      onClick={() => {
                        setLoading(true);
                        setError(null);
                        hasTriedAutoCreate.current = false;
                        fetchStats();
                      }}
                      className="px-4 py-2 bg-gray-900 dark:bg-white text-white dark:text-gray-900 font-inter-tight font-bold text-sm rounded-[5px] hover:bg-gray-800 dark:hover:bg-gray-100"
                    >
                      {t('aff.dash.tryAgain')}
                    </button>
                  </>
                ) : (
                  <>
                    <div className="w-12 h-12 mx-auto mb-4 border-4 border-gray-300 dark:border-white/30 border-t-transparent rounded-full animate-spin" />
                    <p className="text-gray-600 dark:text-gray-400 font-satoshi">
                      {t('aff.dash.loadingSetup')}
                    </p>
                  </>
                )}
              </motion.div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-40 bg-gray-50 dark:bg-[#0f0f12]">
      {/* Adjust for sidebar */}
      <div className={`absolute inset-0 transition-[left] duration-300 ${isCollapsed ? 'lg:left-20' : 'lg:left-64'}`}>
        {/* Header spacer — sized to match the scroll-area offset below */}
        <div className="h-14 lg:h-16" />

        {/* Scrollable Content — mobile header is 3.5rem (h-14), desktop is
            4rem (h-16). Pairing those exactly means the inner scroll area
            exactly fills the remainder of the visual viewport with no
            dead strip at the bottom. */}
        <div className="h-[calc(100dvh-3.5rem)] lg:h-[calc(100dvh-4rem)] overflow-auto">
          <div className="relative z-10 py-4 px-4 lg:px-6 pb-24 lg:pb-8">
        {/* Header */}
        <div className="mb-4">
          <h2 className="text-2xl lg:text-3xl font-monument font-bold text-gray-900 dark:text-white mb-1">
            {t('aff.dash.title')}
          </h2>
          <p className="text-gray-600 dark:text-gray-400 font-satoshi text-sm">
            {t('aff.dash.subtitle', { pct: Math.round(stats.commissionRate) })}
          </p>
        </div>

        {/* Top-level page navigation */}
        <div className="flex gap-2 mb-6">
          <Link
            href="/affiliate"
            className={`flex items-center gap-2 px-5 py-2.5 rounded-[5px] text-sm font-inter-tight font-bold transition-colors ${
              currentPage === 'dashboard'
                ? 'bg-gray-900 dark:bg-white text-white dark:text-black'
                : 'bg-white dark:bg-[#1A1A1A] text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
            }`}
          >
            <DollarSignIcon size={16} className={currentPage === 'dashboard' ? 'text-white dark:text-black' : 'text-gray-400'} />
            {t('aff.dash.tab.dashboard')}
          </Link>
          <Link
            href="/affiliate/toolkit"
            className={`flex items-center gap-2 px-5 py-2.5 rounded-[5px] text-sm font-inter-tight font-bold transition-colors ${
              currentPage === 'toolkit'
                ? 'bg-gray-900 dark:bg-white text-white dark:text-black'
                : 'bg-white dark:bg-[#1A1A1A] text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
            }`}
          >
            <FiPackage className={`w-4 h-4 ${currentPage === 'toolkit' ? 'text-white dark:text-black' : 'text-gray-400'}`} />
            {t('aff.dash.tab.brandKit')}
          </Link>
        </div>

        {currentPage === 'toolkit' ? (
          <AffiliateToolkit />
        ) : (
        <div className="grid grid-cols-1 xl:grid-cols-12 gap-6">
          {/* Main Content - Left Side */}
          <div className="xl:col-span-8 space-y-6">
            {/* Stats Row - Compact */}
            <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
                className="bg-white dark:bg-[#18181D] rounded-[5px] border border-gray-200 dark:border-[#1A1A1A] transition-colors p-4"
              >
                <div className="flex items-center gap-2 mb-1">
                  <DollarSignIcon size={16} className="text-[#00FF9D]" />
                  <span className="text-xs font-inter-tight text-gray-500 dark:text-gray-400 uppercase">{t('aff.dash.stat.available')}</span>
                </div>
                <p className="text-xl lg:text-2xl font-monument font-bold text-gray-900 dark:text-white tabular-nums">
                  $<AnimatedNumber value={stats.availableBalance} decimals={4} />
                </p>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.15 }}
                className="bg-white dark:bg-[#18181D] rounded-[5px] border border-gray-200 dark:border-[#1A1A1A] transition-colors p-4"
              >
                <div className="flex items-center gap-2 mb-1">
                  <FiTrendingUp className="w-4 h-4 text-emerald-500" />
                  <span className="text-xs font-inter-tight text-gray-500 dark:text-gray-400 uppercase">{t('aff.dash.stat.earned')}</span>
                </div>
                <p className="text-xl lg:text-2xl font-monument font-bold text-gray-900 dark:text-white tabular-nums">
                  $<AnimatedNumber value={stats.totalEarnings} decimals={4} />
                </p>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.18 }}
                className="bg-white dark:bg-[#18181D] rounded-[5px] border border-gray-200 dark:border-[#1A1A1A] transition-colors p-4"
              >
                <div className="flex items-center gap-2 mb-1">
                  <FiStar className="w-4 h-4 text-yellow-500" />
                  <span className="text-xs font-inter-tight text-gray-500 dark:text-gray-400 uppercase">{t('aff.dash.stat.fp')}</span>
                </div>
                <p className="text-xl lg:text-2xl font-monument font-bold text-gray-900 dark:text-white tabular-nums">
                  <AnimatedNumber value={stats.totalFPEarned || 0} decimals={1} />
                </p>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="bg-white dark:bg-[#18181D] rounded-[5px] border border-gray-200 dark:border-[#1A1A1A] transition-colors p-4"
              >
                <div className="flex items-center gap-2 mb-1">
                  <UsersIcon size={16} className="text-blue-500" />
                  <span className="text-xs font-inter-tight text-gray-500 dark:text-gray-400 uppercase">{t('aff.dash.stat.referrals')}</span>
                </div>
                <p className="text-xl lg:text-2xl font-monument font-bold text-gray-900 dark:text-white tabular-nums">
                  <AnimatedNumber value={stats.activeReferrals} />
                  <span className="text-sm text-gray-500 font-satoshi font-normal ml-1">/ <AnimatedNumber value={stats.totalReferrals} /></span>
                </p>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.25 }}
                className="bg-white dark:bg-[#18181D] rounded-[5px] border border-gray-200 dark:border-[#1A1A1A] transition-colors p-4"
              >
                <div className="flex items-center gap-2 mb-1">
                  <FiExternalLink className="w-4 h-4 text-purple-500" />
                  <span className="text-xs font-inter-tight text-gray-500 dark:text-gray-400 uppercase">{t('aff.dash.stat.clicks')}</span>
                </div>
                <p className="text-xl lg:text-2xl font-monument font-bold text-gray-900 dark:text-white tabular-nums">
                  <AnimatedNumber value={stats.totalClicks} />
                  <span className="text-sm text-gray-500 font-satoshi font-normal ml-1">(<AnimatedNumber value={stats.conversionRate} />%)</span>
                </p>
              </motion.div>
            </div>

            {/* Tabs + Table */}
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="bg-white dark:bg-[#18181D] rounded-[5px] border border-gray-200 dark:border-[#1A1A1A] transition-colors overflow-hidden"
            >
              {/* Tab Header */}
              <div className="flex border-b border-gray-200 dark:border-[#1A1A1A]">
                <button
                  onClick={() => setActiveTab('referrals')}
                  className={`flex-1 px-2 sm:px-4 py-3 text-xs sm:text-sm whitespace-nowrap font-inter-tight font-bold transition-colors ${
                    activeTab === 'referrals'
                      ? 'text-gray-900 dark:text-white border-b-2 border-gray-900 dark:border-white bg-gray-50 dark:bg-[#0f0f12]'
                      : 'text-gray-500 hover:text-gray-700 dark:hover:text-gray-300'
                  }`}
                >
                  {t('aff.dash.tabs.referrals', { n: referrals.length })}
                </button>
                <button
                  onClick={() => setActiveTab('commissions')}
                  className={`flex-1 px-2 sm:px-4 py-3 text-xs sm:text-sm whitespace-nowrap font-inter-tight font-bold transition-colors ${
                    activeTab === 'commissions'
                      ? 'text-gray-900 dark:text-white border-b-2 border-gray-900 dark:border-white bg-gray-50 dark:bg-[#0f0f12]'
                      : 'text-gray-500 hover:text-gray-700 dark:hover:text-gray-300'
                  }`}
                >
                  {t('aff.dash.tabs.commissions', { n: commissions.length })}
                </button>
                <button
                  onClick={() => setActiveTab('payouts')}
                  className={`flex-1 px-2 sm:px-4 py-3 text-xs sm:text-sm whitespace-nowrap font-inter-tight font-bold transition-colors ${
                    activeTab === 'payouts'
                      ? 'text-gray-900 dark:text-white border-b-2 border-gray-900 dark:border-white bg-gray-50 dark:bg-[#0f0f12]'
                      : 'text-gray-500 hover:text-gray-700 dark:hover:text-gray-300'
                  }`}
                >
                  {t('aff.dash.tabs.payouts', { n: payoutHistory.length })}
                </button>
              </div>

              {/* Referrals Tab Content */}
              {activeTab === 'referrals' && (
                <>
                  {referrals.length === 0 ? (
                    <div className="p-8 text-center">
                      <div className="w-12 h-12 mx-auto mb-3 rounded-[5px] bg-gray-100 dark:bg-[#1A1A1A] flex items-center justify-center">
                        <UsersIcon size={24} className="text-gray-400" />
                      </div>
                      <p className="text-gray-600 dark:text-gray-400 font-satoshi text-sm">
                        {t('aff.dash.referrals.empty')}
                      </p>
                    </div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead className="bg-gray-50 dark:bg-[#0f0f12]">
                          <tr>
                            <th className="px-4 py-2.5 text-left text-xs font-inter-tight font-bold text-gray-500 dark:text-gray-400 uppercase">{t('aff.dash.col.wallet')}</th>
                            {hasMultiTier && (
                              <th className="px-4 py-2.5 text-center text-xs font-inter-tight font-bold text-gray-500 dark:text-gray-400 uppercase">{t('aff.dash.col.level')}</th>
                            )}
                            {hasMultiTier && (
                              <th className="px-4 py-2.5 text-left text-xs font-inter-tight font-bold text-gray-500 dark:text-gray-400 uppercase">{t('aff.dash.col.origin')}</th>
                            )}
                            <th className="px-4 py-2.5 text-center text-xs font-inter-tight font-bold text-gray-500 dark:text-gray-400 uppercase">{t('aff.dash.col.source')}</th>
                            <th className="px-4 py-2.5 text-left text-xs font-inter-tight font-bold text-gray-500 dark:text-gray-400 uppercase">{t('aff.dash.col.date')}</th>
                            <th className="px-4 py-2.5 text-center text-xs font-inter-tight font-bold text-gray-500 dark:text-gray-400 uppercase">{t('aff.dash.col.status')}</th>
                            <th className="px-4 py-2.5 text-right text-xs font-inter-tight font-bold text-gray-500 dark:text-gray-400 uppercase">{t('aff.dash.col.volume')}</th>
                            <th className="px-4 py-2.5 text-right text-xs font-inter-tight font-bold text-gray-500 dark:text-gray-400 uppercase">{t('aff.dash.col.earned')}</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100 dark:divide-[#1A1A1A]">
                          {referrals.map((referral) => (
                            <tr key={referral.id} className="hover:bg-gray-50 dark:hover:bg-[#0f0f12]/50">
                              <td className="px-4 py-3">
                                <span className="font-mono text-sm text-gray-900 dark:text-white">
                                  {referral.wallet}
                                </span>
                              </td>
                              {hasMultiTier && (
                                <td className="px-4 py-3 text-center">
                                  <span className={`inline-flex px-2 py-0.5 text-xs font-inter-tight font-bold rounded-[5px] ${
                                    referral.level === 1
                                      ? 'bg-gray-100 dark:bg-[#0f0f12] text-gray-900 dark:text-white'
                                      : referral.level === 2
                                      ? 'bg-[#00FF9D]/10 text-[#00FF9D]'
                                      : 'bg-blue-500/10 text-blue-500'
                                  }`}>
                                    L{referral.level}
                                  </span>
                                </td>
                              )}
                              {hasMultiTier && (
                                <td className="px-4 py-3">
                                  {referral.parentWallet ? (
                                    <span className="font-mono text-xs text-gray-500 dark:text-gray-400" title={t('aff.dash.parentTooltip')}>
                                      {referral.parentWallet}
                                    </span>
                                  ) : (
                                    <span className="text-xs text-gray-400">—</span>
                                  )}
                                </td>
                              )}
                              <td className="px-4 py-3 text-center">
                                <div className="flex items-center justify-center gap-1">
                                  {(referral.source === 'web' || referral.source === 'both') && (
                                    <FiGlobe className="w-4 h-4 text-blue-500" title={t("aff.dash.source.web")} />
                                  )}
                                  {(referral.source === 'telegram' || referral.source === 'both') && (
                                    <FaTelegram className="w-4 h-4 text-[#0088cc]" title={t("aff.dash.source.telegram")} />
                                  )}
                                </div>
                              </td>
                              <td className="px-4 py-3">
                                <span className="font-satoshi text-sm text-gray-500">
                                  {formatDate(referral.signedUpAt)}
                                </span>
                              </td>
                              <td className="px-4 py-3 text-center">
                                <span className={`inline-flex px-2 py-0.5 text-xs font-inter-tight font-bold rounded-[5px] ${
                                  referral.isActivated
                                    ? 'bg-[#00FF9D]/10 text-[#00FF9D]'
                                    : 'bg-gray-100 dark:bg-[#1A1A1A] text-gray-500'
                                }`}>
                                  {referral.isActivated ? t('aff.dash.status.active') : t('aff.dash.status.pending')}
                                </span>
                              </td>
                              <td className="px-4 py-3 text-right">
                                <span className="font-mono text-sm text-gray-900 dark:text-white">
                                  ${referral.totalVolume.toLocaleString('en-US', { maximumFractionDigits: 0 })}
                                </span>
                              </td>
                              <td className="px-4 py-3 text-right">
                                <span className="font-mono text-sm text-[#00FF9D] font-bold">
                                  ${referral.totalCommission.toFixed(4)}
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </>
              )}

              {/* Commissions Tab Content */}
              {activeTab === 'commissions' && (
                <>
                  {commissions.length === 0 ? (
                    <div className="p-8 text-center">
                      <div className="w-12 h-12 mx-auto mb-3 rounded-[5px] bg-gray-100 dark:bg-[#1A1A1A] flex items-center justify-center">
                        <DollarSignIcon size={24} className="text-gray-400" />
                      </div>
                      <p className="text-gray-600 dark:text-gray-400 font-satoshi text-sm">
                        {t('aff.dash.commissions.empty')}
                      </p>
                    </div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead className="bg-gray-50 dark:bg-[#0f0f12]">
                          <tr>
                            <th className="px-4 py-2.5 text-left text-xs font-inter-tight font-bold text-gray-500 dark:text-gray-400 uppercase">{t('aff.dash.col.date')}</th>
                            <th className="px-4 py-2.5 text-left text-xs font-inter-tight font-bold text-gray-500 dark:text-gray-400 uppercase">{t('aff.dash.col.from')}</th>
                            <th className="px-4 py-2.5 text-center text-xs font-inter-tight font-bold text-gray-500 dark:text-gray-400 uppercase">{t('aff.dash.col.source')}</th>
                            <th className="px-4 py-2.5 text-center text-xs font-inter-tight font-bold text-gray-500 dark:text-gray-400 uppercase">{t('aff.dash.col.level')}</th>
                            <th className="px-4 py-2.5 text-right text-xs font-inter-tight font-bold text-gray-500 dark:text-gray-400 uppercase">{t('aff.dash.col.volume')}</th>
                            <th className="px-4 py-2.5 text-right text-xs font-inter-tight font-bold text-gray-500 dark:text-gray-400 uppercase">{t('aff.dash.col.commission')}</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100 dark:divide-[#1A1A1A]">
                          {commissions.map((commission) => (
                            <tr key={commission.id} className="hover:bg-gray-50 dark:hover:bg-[#0f0f12]/50">
                              <td className="px-4 py-3">
                                <span className="font-satoshi text-sm text-gray-500">
                                  {formatDate(commission.earnedAt)}
                                </span>
                              </td>
                              <td className="px-4 py-3">
                                <span className="font-mono text-sm text-gray-900 dark:text-white">
                                  {commission.referredUser
                                    ? `${commission.referredUser.slice(0, 6)}...${commission.referredUser.slice(-4)}`
                                    : t('aff.dash.anonymous')}
                                </span>
                              </td>
                              <td className="px-4 py-3 text-center">
                                <SourceBadge source={commission.source} />
                              </td>
                              <td className="px-4 py-3 text-center">
                                {commission.commissionType === 'post_author' ? (
                                  // Post-author commissions are stored with
                                  // commission_level=99 internally; surface
                                  // them as a distinct "Post" badge so the
                                  // user sees the difference between fees
                                  // earned on signup-based referrals (L1/L2/L3)
                                  // and fees earned because someone traded
                                  // from one of their shared posts. Optional
                                  // post link added when sourcePostId is
                                  // present so users can jump back to the
                                  // post that drove the commission.
                                  commission.sourcePostId ? (
                                    <a
                                      href={`/post/${commission.sourcePostId}`}
                                      className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-inter-tight font-bold rounded-[5px] bg-gray-100 dark:bg-white/10 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-white/15 transition-colors"
                                      title={t('aff.dash.viewPost')}
                                    >
                                      {t('aff.dash.post')}
                                    </a>
                                  ) : (
                                    <span className="inline-flex px-2 py-0.5 text-xs font-inter-tight font-bold rounded-[5px] bg-gray-100 dark:bg-white/10 text-gray-600 dark:text-gray-300">
                                      {t('aff.dash.post')}
                                    </span>
                                  )
                                ) : (
                                  <span className={`inline-flex px-2 py-0.5 text-xs font-inter-tight font-bold rounded-[5px] ${
                                    commission.level === 1
                                      ? 'bg-gray-100 dark:bg-[#0f0f12] text-gray-900 dark:text-white'
                                      : commission.level === 2
                                      ? 'bg-[#00FF9D]/10 text-[#00FF9D]'
                                      : 'bg-blue-500/10 text-blue-500'
                                  }`}>
                                    L{commission.level}
                                  </span>
                                )}
                              </td>
                              <td className="px-4 py-3 text-right">
                                <span className="font-mono text-sm text-gray-900 dark:text-white">
                                  ${commission.tradeVolume.toLocaleString('en-US', { maximumFractionDigits: 0 })}
                                </span>
                              </td>
                              <td className="px-4 py-3 text-right">
                                <span className="font-mono text-sm text-[#00FF9D] font-bold">
                                  +${commission.amount.toFixed(4)}
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </>
              )}

              {/* Payouts Tab Content */}
              {activeTab === 'payouts' && (
                <>
                  {payoutHistory.length === 0 ? (
                    <div className="p-8 text-center">
                      <div className="w-12 h-12 mx-auto mb-3 rounded-[5px] bg-gray-100 dark:bg-[#1A1A1A] flex items-center justify-center">
                        <FiPackage className="w-6 h-6 text-gray-400" />
                      </div>
                      <p className="text-gray-600 dark:text-gray-400 font-satoshi text-sm">
                        {t('aff.dash.payouts.empty')}
                      </p>
                    </div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead className="bg-gray-50 dark:bg-[#0f0f12]">
                          <tr>
                            <th className="px-4 py-2.5 text-left text-xs font-inter-tight font-bold text-gray-500 dark:text-gray-400 uppercase">{t('aff.dash.col.date')}</th>
                            <th className="px-4 py-2.5 text-right text-xs font-inter-tight font-bold text-gray-500 dark:text-gray-400 uppercase">{t('aff.dash.col.amount')}</th>
                            <th className="px-4 py-2.5 text-center text-xs font-inter-tight font-bold text-gray-500 dark:text-gray-400 uppercase">{t('aff.dash.col.status')}</th>
                            <th className="px-4 py-2.5 text-left text-xs font-inter-tight font-bold text-gray-500 dark:text-gray-400 uppercase">{t('aff.dash.col.destination')}</th>
                            <th className="px-4 py-2.5 text-right text-xs font-inter-tight font-bold text-gray-500 dark:text-gray-400 uppercase">{t('aff.dash.col.tx')}</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100 dark:divide-[#1A1A1A]">
                          {payoutHistory.map((payout: any) => (
                            <tr key={payout.id} className="hover:bg-gray-50 dark:hover:bg-[#0f0f12]/50">
                              <td className="px-4 py-3">
                                <span className="font-satoshi text-sm text-gray-500">
                                  {formatDate(payout.createdAt)}
                                </span>
                              </td>
                              <td className="px-4 py-3 text-right">
                                <span className="font-mono text-sm text-[#00FF9D] font-bold">
                                  ${parseFloat(payout.amount).toFixed(4)}
                                </span>
                              </td>
                              <td className="px-4 py-3 text-center">
                                <PayoutStatusBadge status={payout.status} />
                              </td>
                              <td className="px-4 py-3">
                                <span className="font-mono text-sm text-gray-900 dark:text-white">
                                  {payout.destinationAddress
                                    ? `${payout.destinationAddress.slice(0, 6)}...${payout.destinationAddress.slice(-4)}`
                                    : '\u2014'}
                                </span>
                              </td>
                              <td className="px-4 py-3 text-right">
                                {payout.txHash ? (
                                  <a
                                    href={`https://polygonscan.com/tx/${payout.txHash}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="inline-flex items-center gap-1 text-xs font-mono text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                                  >
                                    {payout.txHash.slice(0, 6)}...{payout.txHash.slice(-4)}
                                    <FiExternalLink className="w-3 h-3" />
                                  </a>
                                ) : (
                                  <span className="text-sm text-gray-400">&mdash;</span>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      {/* Rejection reasons shown below table for rejected payouts */}
                      {payoutHistory.some((p: any) => p.rejectionReason) && (
                        <div className="px-4 py-3 border-t border-gray-100 dark:border-[#1A1A1A] space-y-1">
                          {payoutHistory.filter((p: any) => p.rejectionReason).map((payout: any) => (
                            <p key={payout.id} className="text-xs text-red-500">
                              {t('aff.dash.rejected', { amt: parseFloat(payout.amount).toFixed(4), reason: payout.rejectionReason })}
                            </p>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </>
              )}
            </motion.div>
          </div>

          {/* Sidebar - Right Side */}
          <div className="xl:col-span-4">
            <div className="xl:sticky xl:top-4 space-y-4">
              {/* Referral Link Card */}
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="bg-white dark:bg-[#18181D] rounded-[5px] border border-gray-200 dark:border-[#1A1A1A] transition-colors p-4"
              >
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-[5px] bg-gray-900 dark:bg-white flex items-center justify-center">
                      <TicketIcon size={16} className="text-[#0A0A0A]" />
                    </div>
                    <div>
                      <p className="text-xs font-inter-tight text-gray-500 dark:text-gray-400 uppercase">{t('aff.dash.linkCard.yourLink')}</p>
                      <p className="text-sm font-monument font-bold text-gray-900 dark:text-white">{stats.referralCode}</p>
                    </div>
                  </div>
                </div>

                {/* Web link */}
                <p className="text-[10px] font-inter-tight font-bold text-gray-500 dark:text-gray-400 uppercase mb-1.5">{t('aff.dash.linkCard.webLabel')}</p>
                <div className="flex items-center gap-2 mb-3">
                  <div className="flex-1 min-w-0 bg-gray-50 dark:bg-[#0f0f12] rounded-[5px] p-2 border border-gray-200 dark:border-[#1A1A1A]">
                    <code className="block text-xs text-gray-700 dark:text-gray-300 font-mono truncate">
                      {getReferralLink()}
                    </code>
                  </div>
                  <button
                    onClick={copyReferralLink}
                    aria-label={t('aff.dash.linkCard.copy')}
                    className={`flex-shrink-0 flex items-center justify-center w-9 h-9 rounded-[5px] transition-colors ${
                      copied
                        ? 'bg-[#00FF9D]/10 text-[#00FF9D] border border-[#00FF9D]/30'
                        : 'bg-gray-100 dark:bg-[#0f0f12] text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-[#1A1A1A] hover:bg-gray-200 dark:hover:bg-[#1A1A1A]'
                    }`}
                  >
                    {copied ? <FiCheck className="w-4 h-4" /> : <FiCopy className="w-4 h-4" />}
                  </button>
                </div>

                {/* Telegram bot link */}
                <p className="text-[10px] font-inter-tight font-bold text-gray-500 dark:text-gray-400 uppercase mb-1.5">{t('aff.dash.linkCard.telegramLabel')}</p>
                <div className="flex items-center gap-2 mb-3">
                  <div className="flex-1 min-w-0 bg-gray-50 dark:bg-[#0f0f12] rounded-[5px] p-2 border border-gray-200 dark:border-[#1A1A1A]">
                    <code className="block text-xs text-gray-700 dark:text-gray-300 font-mono truncate">
                      {getTelegramBotLink()}
                    </code>
                  </div>
                  <button
                    onClick={copyTelegramBotLink}
                    aria-label={t('aff.dash.linkCard.copy')}
                    className={`flex-shrink-0 flex items-center justify-center w-9 h-9 rounded-[5px] transition-colors ${
                      copiedTg
                        ? 'bg-[#00FF9D]/10 text-[#00FF9D] border border-[#00FF9D]/30'
                        : 'bg-gray-100 dark:bg-[#0f0f12] text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-[#1A1A1A] hover:bg-gray-200 dark:hover:bg-[#1A1A1A]'
                    }`}
                  >
                    {copiedTg ? <FiCheck className="w-4 h-4" /> : <FiCopy className="w-4 h-4" />}
                  </button>
                </div>

                {/* Share actions */}
                <div className="grid grid-cols-2 gap-2">
                  <button
                    onClick={shareToTwitter}
                    className="flex items-center justify-center gap-1.5 px-3 py-2 rounded-[5px] text-xs font-inter-tight font-bold bg-gray-100 dark:bg-[#0f0f12] text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-[#1A1A1A] hover:bg-gray-200 dark:hover:bg-[#1A1A1A] transition-colors"
                  >
                    <FaXTwitter className="w-3.5 h-3.5" />
                    {t('aff.dash.linkCard.shareX')}
                  </button>
                  <button
                    onClick={shareToTelegram}
                    className="flex items-center justify-center gap-1.5 px-3 py-2 rounded-[5px] text-xs font-inter-tight font-bold bg-gray-100 dark:bg-[#0f0f12] text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-[#1A1A1A] hover:bg-gray-200 dark:hover:bg-[#1A1A1A] transition-colors"
                  >
                    <FaTelegram className="w-3.5 h-3.5" />
                    {t('aff.dash.linkCard.shareTg')}
                  </button>
                </div>
              </motion.div>

              {/* Commission Tiers Card */}
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.25 }}
                className="bg-white dark:bg-[#18181D] rounded-[5px] border border-gray-200 dark:border-[#1A1A1A] transition-colors p-4"
              >
                <h3 className="text-xs font-inter-tight font-bold text-gray-500 dark:text-gray-400 uppercase mb-3">
                  {t('aff.dash.rewards.title')}
                </h3>

                <div className="space-y-2">
                  {/* Fees Commission */}
                  <div className="flex items-center justify-between py-2 border-b border-gray-100 dark:border-[#1A1A1A]">
                    <div className="flex items-center gap-2">
                      <DollarSignIcon size={16} className="text-gray-500 dark:text-gray-400" />
                      <span className="text-sm text-gray-600 dark:text-gray-400">{t('aff.dash.rewards.fees')}</span>
                    </div>
                    <span className="text-sm font-bold text-gray-900 dark:text-white">{Math.round(stats.tier?.level1Percent || 10)}%</span>
                  </div>
                  {/* FP Bonus */}
                  <div className="flex items-center justify-between py-2 border-b border-gray-100 dark:border-[#1A1A1A]">
                    <div className="flex items-center gap-2">
                      <FiStar className="w-4 h-4 text-yellow-500" />
                      <span className="text-sm text-gray-600 dark:text-gray-400">{t('aff.dash.rewards.fp')}</span>
                    </div>
                    <span className="text-sm font-bold text-yellow-500">+10 FP</span>
                  </div>
                  {(stats.tier?.level2Percent ?? 0) > 0 && (
                    <div className="flex items-center justify-between py-2 border-b border-gray-100 dark:border-[#1A1A1A]">
                      <div className="flex items-center gap-2">
                        <span className="w-2 h-2 rounded-[5px] bg-[#00FF9D]" />
                        <span className="text-sm text-gray-600 dark:text-gray-400">{t('aff.dash.rewards.l2')}</span>
                      </div>
                      <span className="text-sm font-bold text-gray-900 dark:text-white">{stats.tier?.level2Percent}%</span>
                    </div>
                  )}
                  {(stats.tier?.level3Percent ?? 0) > 0 && (
                    <div className="flex items-center justify-between py-2">
                      <div className="flex items-center gap-2">
                        <span className="w-2 h-2 rounded-[5px] bg-blue-500" />
                        <span className="text-sm text-gray-600 dark:text-gray-400">{t('aff.dash.rewards.l3')}</span>
                      </div>
                      <span className="text-sm font-bold text-gray-900 dark:text-white">{stats.tier?.level3Percent}%</span>
                    </div>
                  )}
                </div>

                <div className="mt-3 pt-3 border-t border-gray-100 dark:border-[#1A1A1A]">
                  <p className="text-xs text-gray-500 dark:text-gray-400 leading-relaxed">
                    {t('aff.dash.rewards.note', { pct: Math.round(stats.commissionRate) })}
                  </p>
                </div>
              </motion.div>

              {/* Quick Stats */}
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="bg-white dark:bg-[#18181D] rounded-[5px] border border-gray-200 dark:border-[#1A1A1A] transition-colors p-4"
              >
                <h3 className="text-xs font-inter-tight font-bold text-gray-500 dark:text-gray-400 uppercase mb-3">
                  {t('aff.dash.last30')}
                </h3>
                <div className="grid grid-cols-3 gap-3">
                  <div className="text-center p-2 bg-gray-50 dark:bg-[#0f0f12] rounded-[5px]">
                    <p className="text-lg font-monument font-bold text-gray-900 dark:text-white tabular-nums"><AnimatedNumber value={stats.clicksLast30Days} /></p>
                    <p className="text-xs text-gray-500">{t('aff.dash.metrics.clicks')}</p>
                  </div>
                  <div className="text-center p-2 bg-gray-50 dark:bg-[#0f0f12] rounded-[5px]">
                    <p className="text-lg font-monument font-bold text-gray-900 dark:text-white tabular-nums"><AnimatedNumber value={stats.signupsLast30Days} /></p>
                    <p className="text-xs text-gray-500">{t('aff.dash.metrics.signups')}</p>
                  </div>
                  <div className="text-center p-2 bg-gray-50 dark:bg-[#0f0f12] rounded-[5px]">
                    {/* Reads the server-aggregated 30-day total instead of
                        summing the paginated commissions array — the latter
                        only saw the first page (10 of 188 for high-volume
                        referrers like marc) and under-reported by 6×. */}
                    <p className="text-lg font-monument font-bold text-[#00FF9D] tabular-nums">
                      $<AnimatedNumber value={stats.earningsLast30Days} decimals={2} />
                    </p>
                    <p className="text-xs text-gray-500">{t('aff.dash.metrics.earned')}</p>
                  </div>
                </div>
              </motion.div>

              {/* Payout Card */}
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.35 }}
                className="bg-white dark:bg-[#18181D] rounded-[5px] border border-gray-200 dark:border-[#1A1A1A] transition-colors p-4"
              >
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-xs font-inter-tight font-bold text-gray-500 dark:text-gray-400 uppercase">
                    {t('aff.dash.payout.title')}
                  </h3>
                  <span className="text-sm font-mono text-[#00FF9D] font-bold tabular-nums">
                    $<AnimatedNumber value={stats.availableBalance} decimals={4} />
                  </span>
                </div>
                <button
                  onClick={() => setShowPayoutModal(true)}
                  disabled={stats.availableBalance <= 0}
                  className={`w-full py-2.5 rounded-[5px] text-sm font-inter-tight font-bold transition-colors ${
                    stats.availableBalance > 0
                      ? 'bg-gray-900 dark:bg-white hover:bg-gray-800 dark:hover:bg-gray-100 text-white dark:text-gray-900'
                      : 'bg-gray-100 dark:bg-[#1A1A1A] text-gray-400 cursor-not-allowed'
                  }`}
                >
                  {stats.availableBalance > 0 ? t('aff.dash.payout.request') : t('aff.dash.payout.noBalance')}
                </button>
                <p className="text-xs text-gray-500 text-center mt-2">
                  {t('aff.dash.payout.processedIn')}
                </p>
              </motion.div>

            </div>
          </div>
        </div>
        )}
          </div>
        </div>
      </div>

      {/* Request Payout Modal */}
      <RequestPayoutModal
        isOpen={showPayoutModal}
        onClose={() => setShowPayoutModal(false)}
        availableBalance={stats.availableBalance}
        walletAddress={walletAddress}
        onSuccess={() => {
          fetchStats();
          fetchPayoutHistory();
        }}
      />
    </div>
  );
}
